import { useEffect, type ReactNode } from "react";
import { Link } from "wouter";
import { LINKS_NAV, RODAPE } from "./conteudo";

/**
 * As peças comuns das páginas PÚBLICAS do site (landing, /termos, /privacidade):
 * logo, nav fixa, rodapé e a fonte dos títulos. Fora do `AppLayout` de propósito —
 * aqui não há sessão, sidebar nem trial wall.
 */

export function RizomaLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Rizoma"
    >
      <rect width="44" height="44" rx="9" fill="#2D5016" />
      <circle cx="14" cy="22" r="4.5" fill="white" />
      <circle cx="27" cy="14" r="3.5" fill="white" opacity="0.75" />
      <circle cx="27" cy="30" r="3.5" fill="white" opacity="0.75" />
      <circle cx="37" cy="22" r="2.5" fill="#C17BC0" opacity="0.9" />
      <line x1="14" y1="22" x2="24" y2="15" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="14" y1="22" x2="24" y2="29" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="27" y1="14" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
      <line x1="27" y1="30" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
    </svg>
  );
}

const ID_FONTE_TITULOS = "fonte-titulos-landing";
const URL_FONTE_TITULOS =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@600;800&display=swap";

/**
 * Carrega a Bricolage Grotesque (600/800) SÓ quando uma página pública monta.
 *
 * Não vai no `index.html` nem no `@import` do index.css porque o mesmo bundle
 * serve o app do campo, que não usa a fonte e roda em 3G. O `<link>` fica no
 * `<head>` depois de montado (idempotente pelo `id`): sair e voltar para a
 * landing não baixa de novo, e quem nunca abre a landing nunca baixa.
 * Sem rede, `--font-titulo` cai na Inter — a página não espera pela fonte.
 */
export function useFonteTitulos(): void {
  useEffect(() => {
    if (document.getElementById(ID_FONTE_TITULOS)) return;
    const link = document.createElement("link");
    link.id = ID_FONTE_TITULOS;
    link.rel = "stylesheet";
    link.href = URL_FONTE_TITULOS;
    document.head.appendChild(link);
  }, []);
}

/**
 * Título e descrição da aba. O `index.html` já traz os da landing (é o que
 * WhatsApp e buscadores leem, sem rodar JS); isto só troca o título nas
 * páginas irmãs e o devolve ao sair.
 */
export function useTituloDaPagina(titulo: string): void {
  useEffect(() => {
    const anterior = document.title;
    document.title = titulo;
    return () => {
      document.title = anterior;
    };
  }, [titulo]);
}

/**
 * Nav fixa. Os links-âncora só existem na landing; nas páginas irmãs eles
 * apontam para `/#secao` — que leva de volta à landing já na seção.
 * Abaixo de 820 px os links somem e ficam só "Entrar" e "Começar grátis".
 */
export function NavPublica({ naLanding }: { naLanding: boolean }) {
  const prefixo = naLanding ? "" : "/";
  return (
    <header className="sticky top-0 z-30 border-b border-border/70 bg-landing-fundo/90 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
        <Link href="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <RizomaLogo className="h-8 w-8" />
          <span className="font-titulo font-extrabold text-lg tracking-tight text-landing-folha">Rizoma</span>
        </Link>
        <nav className="hidden min-[820px]:flex items-center gap-6 text-sm text-muted-foreground" aria-label="Seções">
          {LINKS_NAV.map((l) => (
            <a key={l.href} href={`${prefixo}${l.href}`} className="hover:text-foreground transition-colors">
              {l.rotulo}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            href="/login"
            className="px-3 py-2 text-sm font-medium text-foreground rounded-lg hover:bg-foreground/5 transition-colors"
            data-testid="nav-entrar"
          >
            Entrar
          </Link>
          <Link
            href="/signup"
            className="px-3.5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
            data-testid="nav-comecar"
          >
            Começar grátis
          </Link>
        </div>
      </div>
    </header>
  );
}

export function RodapePublico() {
  return (
    <footer className="border-t border-border py-8 px-4 text-sm text-muted-foreground bg-landing-fundo">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <RizomaLogo className="h-6 w-6" />
          <span>{RODAPE.copyright}</span>
        </div>
        <nav className="flex items-center gap-5" aria-label="Documentos">
          {RODAPE.links.map((l) => (
            <Link key={l.href} href={l.href} className="hover:text-foreground transition-colors">
              {l.rotulo}
            </Link>
          ))}
        </nav>
      </div>
    </footer>
  );
}

/** Casca das páginas públicas: fundo, nav, conteúdo e rodapé. */
export function PaginaPublica({ naLanding, children }: { naLanding: boolean; children: ReactNode }) {
  useFonteTitulos();
  return (
    <div className="min-h-screen flex flex-col bg-landing-fundo text-foreground">
      <NavPublica naLanding={naLanding} />
      <main className="flex-1">{children}</main>
      <RodapePublico />
    </div>
  );
}
